import { z } from 'zod'
import {
  contentTypes,
  deliveryChannels,
  isValidSemVer,
  rightsLevels,
  softwareStylePageKeys,
  systemDomains
} from '@bf/domain'
import { isPortableMediaPath } from './media-path-policy'

const deliveryChannelSchema = z.enum(deliveryChannels)
const rightsLevelSchema = z.enum(rightsLevels)
const systemDomainSchema = z.enum(systemDomains)
const contentTypeSchema = z.enum(contentTypes)

const publishedMetaSchema = z.object({
  id: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  contentType: contentTypeSchema,
  title: z.string().min(1),
  summary: z.string().min(1),
  systemDomains: z.array(systemDomainSchema).min(1),
  status: z.literal('approved'),
  contentVersion: z.string().min(1),
  rightsLevel: rightsLevelSchema,
  allowedChannels: z.array(deliveryChannelSchema).min(1),
  expiresAt: z.iso.datetime().nullable(),
  tags: z.array(z.string()),
  sortOrder: z.int().nonnegative(),
  mediaIds: z.array(z.string())
})

const companyProfileSchema = publishedMetaSchema.extend({
  contentType: z.literal('company'),
  tagline: z.string(),
  facts: z
    .array(
      z.object({
        value: z.string().min(1),
        label: z.string().min(1)
      })
    )
    .optional(),
  values: z.array(z.string().min(1)).optional(),
  capabilities: z.array(
    z.object({
      domain: systemDomainSchema,
      title: z.string().min(1),
      description: z.string().min(1)
    })
  ),
  contact: z.object({
    name: z.string().optional(),
    phone: z.string(),
    website: z.string().optional(),
    email: z.string(),
    address: z.string()
  })
})

const productSchema = publishedMetaSchema.extend({
  contentType: z.literal('product'),
  productCategory: z.enum(['cabinet', 'ddc-controller', 'terminal-device']),
  model: z.string(),
  highlights: z.array(z.string()),
  parameters: z.array(
    z.object({
      label: z.string().min(1),
      value: z.string().min(1)
    })
  ),
  applicationScenarios: z.array(z.string())
})

const softwareStyleSchema = publishedMetaSchema.extend({
  contentType: z.literal('software-style'),
  presentationMode: z.enum([
    'interactive-sample',
    'static-introduction',
    'style-package'
  ]),
  styleCode: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  themeLabel: z.string(),
  palette: z.object({
    background: z.string().regex(/^#[0-9a-f]{6}$/i),
    surface: z.string().regex(/^#[0-9a-f]{6}$/i),
    primary: z.string().regex(/^#[0-9a-f]{6}$/i),
    accent: z.string().regex(/^#[0-9a-f]{6}$/i),
    text: z.string().regex(/^#[0-9a-f]{6}$/i)
  }),
  recommendedFor: z.array(z.string().min(1)).min(1),
  pagePreviews: z.array(
    z.object({
      pageKey: z.enum(softwareStylePageKeys),
      pageLabel: z.string().min(1),
      mediaId: z.string().min(1),
      customerIntro: z.string().min(1, '业务面客户介绍不能为空'),
      capabilities: z
        .array(z.string().min(1, '业务面功能点不能为空'))
        .min(3, '每个业务面至少需要 3 个功能点')
        .max(5, '每个业务面最多允许 5 个功能点'),
      customerValue: z.string().min(1, '业务面客户价值不能为空')
    })
  ),
  pages: z.array(z.string()),
  highlights: z.array(z.string())
})

const softwareComponentSchema = publishedMetaSchema.extend({
  contentType: z.literal('software-component'),
  problem: z.string(),
  value: z.string(),
  interactionType: z.enum(['status', 'trend', 'alarm', 'schedule', 'report'])
})

const projectCaseSchema = publishedMetaSchema.extend({
  contentType: z.literal('project-case'),
  buildingType: z.string(),
  locationLabel: z.string(),
  challenge: z.string(),
  solution: z.string(),
  projectFacts: z
    .array(
      z.object({
        label: z.string().min(1),
        value: z.string().min(1),
        description: z.string().min(1).optional()
      })
    )
    .optional(),
  controlDescriptions: z
    .array(
      z.object({
        system: z.string().min(1),
        strategy: z.string().min(1)
      })
    )
    .optional(),
  outcomes: z.array(z.string()),
  relatedEntityIds: z.array(z.string()),
  demoData: z.boolean()
})

export const contentEntitySchema = z.discriminatedUnion('contentType', [
  companyProfileSchema,
  productSchema,
  softwareStyleSchema,
  softwareComponentSchema,
  projectCaseSchema
])

const safeMimeTypes = {
  image: new Set([
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
    'image/avif'
  ]),
  video: new Set(['video/mp4', 'video/webm']),
  document: new Set([
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ])
} as const

export const mediaAssetSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    mediaType: z.enum(['image', 'video', 'document']),
    path: z
      .string()
      .min(1)
      .refine(
        isPortableMediaPath,
        '媒体路径必须是可跨 Windows 与 H5 使用的安全相对路径'
      ),
    mimeType: z
      .string()
      .regex(
        /^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/i,
        '媒体 MIME 类型格式无效'
      ),
    bytes: z.int().positive(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    altText: z.string().min(1),
    rightsLevel: rightsLevelSchema,
    allowedChannels: z.array(deliveryChannelSchema).min(1),
    sourceLabel: z.string().min(1),
    sourceReference: z.string().min(1),
    provenance: z.enum([
      'company-owned',
      'authorized-third-party',
      'ai-generated',
      'unknown'
    ]),
    conceptVisual: z.boolean(),
    pendingReviewFields: z.array(z.string().min(1)),
    displayRole: z.enum([
      'cover',
      'gallery',
      'cabinet-layout',
      'thumbnail',
      'layer',
      'document'
    ]),
    layoutTemplateId: z.string().min(1).nullable(),
    preferred: z.boolean()
  })
  .superRefine((asset, context) => {
    if (!safeMimeTypes[asset.mediaType].has(asset.mimeType)) {
      context.addIssue({
        code: 'custom',
        path: ['mimeType'],
        message: `媒体类型 ${asset.mediaType} 不允许使用 MIME ${asset.mimeType}`
      })
    }
    if (asset.provenance === 'ai-generated' && !asset.conceptVisual) {
      context.addIssue({
        code: 'custom',
        path: ['conceptVisual'],
        message: 'AI 生成素材必须标记为概念示意'
      })
    }
    if (
      asset.provenance === 'unknown' &&
      asset.pendingReviewFields.length === 0
    ) {
      context.addIssue({
        code: 'custom',
        path: ['pendingReviewFields'],
        message: '来源待核素材必须列出至少一项待核字段'
      })
    }
    if (
      ['cabinet-layout', 'thumbnail', 'layer'].includes(asset.displayRole) &&
      asset.mediaType !== 'image'
    ) {
      context.addIssue({
        code: 'custom',
        path: ['mediaType'],
        message: `${asset.displayRole} 角色只允许使用图片`
      })
    }
    if (asset.displayRole === 'document' && asset.mediaType !== 'document') {
      context.addIssue({
        code: 'custom',
        path: ['mediaType'],
        message: 'document 角色必须使用文档媒体类型'
      })
    }
  })

export const contentManifestSchema = z.object({
  schemaVersion: z.int().positive(),
  contentVersion: z.string().min(1),
  buildId: z.string().min(1),
  channel: deliveryChannelSchema,
  minAppVersion: z
    .string()
    .refine(isValidSemVer, '最低程序版本必须是严格 SemVer 版本号'),
  generatedAt: z.iso.datetime(),
  demoContent: z.boolean(),
  entities: z.array(contentEntitySchema),
  assets: z.array(mediaAssetSchema)
})
